import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  AccountCircle,
  Logout,
  Person,
  Login
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import './UserProfile.css';

const UserProfile: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      handleClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleProfileClick = () => {
    handleClose();
    navigate('/profile');
  };

  // Generate avatar initials
  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email.charAt(0).toUpperCase();
  };

  // Generate avatar color based on email
  const getAvatarColor = (email: string) => {
    const colors = [
      'linear-gradient(135deg, #55cbcd, #3da3a5)',
      'linear-gradient(135deg, #fcb9aa, #f9967e)',
      'linear-gradient(135deg, #a2e1db, #8ad6cc)',
      'linear-gradient(135deg, #f5f4f0, #e8e6e0)',
    ];
    const hash = email.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  // Debug logging
  React.useEffect(() => {
    if (user) {
      console.log('👤 UserProfile - Current user:', {
        email: user.email,
        displayName: user.displayName,
        tier: user.tier,
        id: user.id
      });
    }
  }, [user]);

  if (!user) {
    return (
      <Box className="user-profile">
        <Button
          component={Link}
          to="/login"
          variant="contained"
          className="user-profile-login-button"
          startIcon={<Login />}
        >
          Login
        </Button>
      </Box>
    );
  }

  return (
    <Box className="user-profile">
      <IconButton
        onClick={handleClick}
        className="user-profile-button"
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Avatar
          className="user-profile-avatar"
          style={{
            background: getAvatarColor(user.email)
          }}
        >
          {getInitials(user.displayName, user.email)}
        </Avatar>
      </IconButton>

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className="user-profile-menu"
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          className: 'user-profile-menu-paper'
        }}
      >
        <Box className="user-profile-menu-header">
          <Typography variant="subtitle2" className="user-profile-name">
            {user.displayName || 'User'}
          </Typography>
          <Typography variant="body2" className="user-profile-email">
            {user.email}
          </Typography>
        </Box>

        <Divider className="user-profile-divider" />

        <MenuItem onClick={handleProfileClick} className="user-profile-menu-item">
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Profile</Typography>
          </ListItemText>
        </MenuItem>

        <Divider className="user-profile-divider" />

        <MenuItem onClick={handleLogout} className="user-profile-menu-item user-profile-logout">
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Logout</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserProfile;
